import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { X, Truck, Printer, Loader2, DollarSign, ShoppingBag, CheckCircle2, TrendingUp,
  RotateCcw, Sparkles, Clock, User, Tag as TagIcon, Send, MapPin, Ban, Trash2, Plus, Pencil, Upload } from "lucide-react";
import { adminApi, apiError } from "@/lib/adminApi";
import { fmt, fmtDate, fmtDateTime, StatusBadge, Card, Spinner } from "@/components/admin/shared";
import { ShippingLabel } from "@/components/admin/ShippingLabel";

const STAT_CARDS = (s) => [
  [DollarSign, "Revenue (paid)", fmt(s.revenue)],
  [ShoppingBag, "Total Orders", s.orders],
  [CheckCircle2, "Paid Orders", s.paid],
  [TrendingUp, "Avg Order Value", fmt(s.aov)],
];

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const inputCls = "w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none";

function OrderFormModal({ mode = "create", order = null, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const isMulti = (order?.items?.length || 0) > 1;
  const c = order?.customer || {}; const a = c.address || {}; const it = (order?.items || [])[0] || {};
  const [f, setF] = useState(isEdit ? {
    first_name: c.first_name || "", last_name: c.last_name || "",
    email: order?.email && order.email !== "guest" ? order.email : (c.email && c.email !== "guest" ? c.email : ""),
    phone: c.phone || "",
    line1: a.line1 || "", line2: a.line2 || "", suburb: a.suburb || "", state: a.state || "NSW", postcode: a.postcode || "", country: a.country || "AU",
    product_name: it.name || "Leopard Luxe™ LED Light Therapy Mask", price: String(it.price ?? "699"), quantity: String(it.quantity ?? "1"),
    payment_status: order?.payment_status || "paid",
    fulfillment_status: order?.fulfillment_status === "shipped" || order?.status === "shipped" ? "shipped" : "unfulfilled",
    payment_method: order?.payment_method || "card",
    created_at: (order?.created_at || "").slice(0, 10),
    tracking_number: order?.tracking_number || "", carrier: order?.carrier || "Australia Post",
  } : {
    first_name: "", last_name: "", email: "", phone: "",
    line1: "", line2: "", suburb: "", state: "NSW", postcode: "", country: "AU",
    product_name: "Leopard Luxe™ LED Light Therapy Mask", price: "699", quantity: "1",
    payment_status: "paid", fulfillment_status: "shipped", payment_method: "card",
    created_at: "", tracking_number: "", carrier: "Australia Post",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const total = (parseFloat(f.price) || 0) * (parseInt(f.quantity, 10) || 1);
  const showProduct = !(isEdit && isMulti);

  const submit = async () => {
    if (!f.first_name.trim()) { toast.error("First name is required"); return; }
    const price = parseFloat(f.price);
    if (showProduct && (!price || price <= 0)) { toast.error("Enter a valid price"); return; }
    setBusy(true);
    try {
      const body = { ...f, created_at: f.created_at || null, tracking_number: f.tracking_number || null };
      if (showProduct) { body.price = price; body.quantity = parseInt(f.quantity, 10) || 1; }
      else { delete body.product_name; delete body.price; delete body.quantity; }
      if (isEdit) { await adminApi.put(`/admin/orders/${order.id}`, body); toast.success("Order updated"); }
      else { await adminApi.post("/admin/orders", body); toast.success("Order added"); }
      onSaved(); onClose();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed to save order"); }
    finally { setBusy(false); }
  };

  const Group = ({ label, children }) => (
    <div>
      <p className="mb-2 text-[0.62rem] font-semibold uppercase tracking-wider text-[#8A7F86]">{label}</p>
      {children}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[130] overflow-y-auto bg-black/40 p-4" data-testid={isEdit ? "edit-order-modal" : "add-order-modal"} onClick={onClose}>
      <div className="mx-auto my-6 w-full max-w-2xl rounded-3xl bg-[#FBF8F3] p-6 shadow-2xl md:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-display text-2xl text-[#2C2B2C]">{isEdit ? "Edit order" : "Add order"}</h2>
          <button onClick={onClose} data-testid="order-form-close" className="rounded-full border border-[#DACDB4] p-1.5 text-[#6E6168] hover:bg-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-5">
          <Group label="Customer">
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="ao-first" value={f.first_name} onChange={set("first_name")} placeholder="First name *" className={inputCls} />
              <input data-testid="ao-last" value={f.last_name} onChange={set("last_name")} placeholder="Last name" className={inputCls} />
              <input data-testid="ao-email" value={f.email} onChange={set("email")} placeholder="Email" className={inputCls} />
              <input data-testid="ao-phone" value={f.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
            </div>
          </Group>
          <Group label="Shipping address">
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="ao-line1" value={f.line1} onChange={set("line1")} placeholder="Street address" className={`${inputCls} sm:col-span-2`} />
              <input data-testid="ao-line2" value={f.line2} onChange={set("line2")} placeholder="Apartment, suite (optional)" className={`${inputCls} sm:col-span-2`} />
              <input data-testid="ao-suburb" value={f.suburb} onChange={set("suburb")} placeholder="Suburb" className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="ao-state" value={f.state} onChange={set("state")} className={inputCls}>
                  {AU_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input data-testid="ao-postcode" value={f.postcode} onChange={set("postcode")} placeholder="Postcode" className={inputCls} />
              </div>
            </div>
          </Group>
          {showProduct ? (
            <Group label="Product">
              <div className="grid gap-3 sm:grid-cols-4">
                <input data-testid="ao-product" value={f.product_name} onChange={set("product_name")} placeholder="Product name" className={`${inputCls} sm:col-span-2`} />
                <input data-testid="ao-price" value={f.price} onChange={set("price")} inputMode="decimal" placeholder="Price" className={inputCls} />
                <input data-testid="ao-qty" value={f.quantity} onChange={set("quantity")} inputMode="numeric" placeholder="Qty" className={inputCls} />
              </div>
              <p className="mt-2 text-sm text-[#6E6168]">Order total: <span className="font-semibold text-[#2C2B2C]" data-testid="ao-total">{fmt(total)}</span></p>
            </Group>
          ) : (
            <p className="rounded-xl border border-[#EDE4D4] bg-white px-3.5 py-2.5 text-xs text-[#8A7F86]">This order has multiple line items — editing customer details, status and date only. Item amounts are left unchanged.</p>
          )}
          <Group label="Status & date">
            <div className="grid gap-3 sm:grid-cols-3">
              <select data-testid="ao-payment" value={f.payment_status} onChange={set("payment_status")} className={inputCls}>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="refunded">Refunded</option>
              </select>
              <select data-testid="ao-fulfillment" value={f.fulfillment_status} onChange={set("fulfillment_status")} className={inputCls}>
                <option value="shipped">Shipped</option>
                <option value="unfulfilled">Unfulfilled</option>
              </select>
              <input data-testid="ao-date" type="date" value={f.created_at} onChange={set("created_at")} className={inputCls} />
            </div>
            {f.fulfillment_status === "shipped" && (
              <input data-testid="ao-tracking" value={f.tracking_number} onChange={set("tracking_number")} placeholder="Tracking number (optional)" className={`${inputCls} mt-3`} />
            )}
          </Group>
          <button onClick={submit} disabled={busy} data-testid={isEdit ? "eo-submit" : "ao-submit"}
            className="flex w-full items-center justify-center gap-1.5 rounded-full bg-[#232222] py-3 text-[0.66rem] uppercase tracking-wider text-[#F5EFE3] transition-opacity disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? "Save changes" : "Add order"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Panel = ({ title, action, children }) => (
  <div className="rounded-2xl border border-[#E5DBC8] bg-white p-5">
    <div className="mb-3 flex items-center justify-between">
      <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-[#8A7F86]">{title}</p>
      {action}
    </div>
    {children}
  </div>
);

function SummaryRow({ label, sub, value, strong }) {
  return (
    <div className="flex items-start justify-between py-1.5 text-sm">
      <div><span className={strong ? "font-semibold text-[#2C2B2C]" : "text-[#6E6168]"}>{label}</span>{sub && <span className="ml-2 text-xs text-[#B6A99F]">{sub}</span>}</div>
      <span className={strong ? "font-semibold text-[#2C2B2C]" : "text-[#2C2B2C]"}>{value}</span>
    </div>
  );
}

function OrderDetail({ orderId, shipping, onClose, onUpdated }) {
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState("");
  const [carrier, setCarrier] = useState("Australia Post");
  const [busy, setBusy] = useState("");
  const [label, setLabel] = useState(false);
  const [note, setNote] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [showRefund, setShowRefund] = useState(false);
  const [refund, setRefund] = useState({ amount: "", reason: "", restock: true });
  const [editing, setEditing] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get(`/admin/orders/${orderId}`);
    setOrder(data);
    setTracking(data.tracking_number || "");
    setCarrier(data.carrier || "Australia Post");
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !label) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, label]);

  if (!order) return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40"><Spinner /></div>
  );

  const cust = order.customer || {};
  const addr = cust.address || {};
  const cur = (order.currency || "aud").toUpperCase();

  const ship = async () => {
    setBusy("ship");
    try {
      await adminApi.post(`/admin/orders/${order.id}/ship`, { tracking_number: tracking || null, carrier });
      toast.success("Order marked as shipped" + (order.email !== "guest" ? " · email sent" : ""));
      await load(); onUpdated();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try { await adminApi.post(`/admin/orders/${order.id}/note`, { text: note }); setNote(""); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const saveTags = async (tags) => {
    try { await adminApi.post(`/admin/orders/${order.id}/tags`, { tags }); setOrder((o) => ({ ...o, tags })); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
  };
  const addTag = () => { const t = tagInput.trim(); if (!t) return; if (!(order.tags || []).includes(t)) saveTags([...(order.tags || []), t]); setTagInput(""); };
  const removeTag = (t) => saveTags((order.tags || []).filter((x) => x !== t));

  const doRefund = async () => {
    setBusy("refund");
    try {
      const body = { amount: refund.amount ? parseFloat(refund.amount) : null, reason: refund.reason, restock: refund.restock };
      const { data } = await adminApi.post(`/admin/orders/${order.id}/refund`, body);
      toast.success(data.processed ? `Refunded via ${data.method === "paypal" ? "PayPal" : "Stripe"}` : "Refund recorded");
      setShowRefund(false); setRefund({ amount: "", reason: "", restock: true });
      await load(); onUpdated();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Refund failed"); }
    finally { setBusy(""); }
  };

  const canRefund = ["paid", "partially_refunded"].includes(order.payment_status);
  const isCancelled = order.status === "cancelled";

  const doCancel = async () => {
    if (!window.confirm(`Cancel order #${order.id.slice(0, 8).toUpperCase()}?${canRefund ? " This does NOT refund the customer — use Refund separately to return their money." : ""}`)) return;
    setBusy("cancel");
    try {
      await adminApi.post(`/admin/orders/${order.id}/cancel`);
      toast.success("Order cancelled");
      await load(); onUpdated();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Cancel failed"); }
    finally { setBusy(""); }
  };

  const del = async () => {
    if (!window.confirm(`Delete order #${order.id.slice(0, 8).toUpperCase()}? This cannot be undone.`)) return;
    setBusy("delete");
    try {
      await adminApi.delete(`/admin/orders/${order.id}`);
      toast.success("Order deleted");
      onClose(); onUpdated();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(""); }
  };

  const toggleExclude = async () => {
    try {
      const { data } = await adminApi.post(`/admin/orders/${order.id}/exclude`, { excluded: !order.exclude_analytics });
      setOrder((o) => ({ ...o, exclude_analytics: data.exclude_analytics }));
      toast.success(data.exclude_analytics ? "Order excluded from analytics" : "Order included in analytics");
      onUpdated();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-[120] overflow-y-auto bg-[#F1EEE9]" data-testid="order-detail">
      <div className="mx-auto max-w-6xl px-5 py-6 md:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <button onClick={onClose} data-testid="order-detail-close" className="rounded-full border border-[#DACDB4] p-1.5 text-[#6E6168] hover:bg-white"><X className="h-4 w-4" /></button>
              <h1 className="font-display text-2xl text-[#2C2B2C]">Order #{order.id.slice(0, 8).toUpperCase()}</h1>
              <StatusBadge status={order.payment_status} />
              <StatusBadge status={order.status === "shipped" ? "shipped" : (order.fulfillment_status || "unfulfilled")} />
            </div>
            <p className="mt-1.5 pl-11 text-xs text-[#8A7F86]">{fmtDateTime(order.created_at)} · from Online Store</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditing(true)} data-testid="edit-order-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#DACDB4] bg-white px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#2C2B2C] hover:border-[#FBC4A8]">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button onClick={toggleExclude} data-testid="exclude-order-toggle"
              className={`flex items-center gap-1.5 rounded-full border px-5 py-2.5 text-[0.62rem] uppercase tracking-wider ${order.exclude_analytics ? "border-[#B07A4E] bg-[#F7EFE6] text-[#B07A4E]" : "border-[#DACDB4] bg-white text-[#6E6168] hover:border-[#FBC4A8]"}`}>
              <Ban className="h-3.5 w-3.5" /> {order.exclude_analytics ? "Excluded from analytics" : "Exclude from analytics"}
            </button>
            {canRefund && (
              <button onClick={() => setShowRefund((v) => !v)} data-testid="refund-toggle"
                className="flex items-center gap-1.5 rounded-full border border-[#DACDB4] bg-white px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#2C2B2C] hover:border-[#FBC4A8]">
                <RotateCcw className="h-3.5 w-3.5" /> Refund
              </button>
            )}
            {!isCancelled && (
              <button onClick={doCancel} disabled={busy === "cancel"} data-testid="cancel-order-btn"
                className="flex items-center gap-1.5 rounded-full border border-[#DACDB4] bg-white px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#B07A4E] hover:border-[#E3B4AC] disabled:opacity-60">
                {busy === "cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />} Cancel
              </button>
            )}
            <button onClick={del} disabled={busy === "delete"} data-testid="delete-order-btn"
              className="flex items-center gap-1.5 rounded-full border border-[#E3B4AC] bg-white px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#C0392B] hover:bg-[#FBEEEC] disabled:opacity-60">
              {busy === "delete" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete
            </button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
          {/* LEFT */}
          <div className="space-y-5">
            <Panel title={order.status === "shipped" ? "Fulfilled" : "Unfulfilled"}>
              <div className="space-y-3">
                {(order.items || []).map((it) => (
                  <div key={it.sku} className="flex items-center gap-3" data-testid={`detail-item-${it.sku}`}>
                    <div className="h-14 w-14 flex-none overflow-hidden rounded-lg border border-[#EDE4D4] bg-[#FFFCFA]">
                      {it.image && <img src={it.image.startsWith("http") ? it.image : it.image} alt={it.name} className="h-full w-full object-cover" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-[#2C2B2C]">{it.name}</p>
                      <p className="text-xs text-[#8A7F86]">{fmt(it.price)} × {it.quantity}</p>
                    </div>
                    <span className="text-sm text-[#2C2B2C]">{fmt(it.line_total)}</span>
                  </div>
                ))}
              </div>
            </Panel>

            {showRefund && canRefund && (
              <Panel title="Issue refund">
                <div className="space-y-3" data-testid="refund-form">
                  <label className="block text-sm">
                    <span className="text-xs text-[#6E6168]">Amount ({cur}) — leave blank for full remaining</span>
                    <input value={refund.amount} onChange={(e) => setRefund((r) => ({ ...r, amount: e.target.value }))} data-testid="refund-amount" inputMode="decimal" placeholder={`${(order.total - (order.refunded_total || 0)).toFixed(2)}`}
                      className="mt-1 w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none" />
                  </label>
                  <label className="block text-sm">
                    <span className="text-xs text-[#6E6168]">Reason (optional)</span>
                    <input value={refund.reason} onChange={(e) => setRefund((r) => ({ ...r, reason: e.target.value }))} data-testid="refund-reason" placeholder="e.g. Customer changed their mind"
                      className="mt-1 w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none" />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-[#6E6168]">
                    <input type="checkbox" checked={refund.restock} onChange={(e) => setRefund((r) => ({ ...r, restock: e.target.checked }))} /> Restock items
                  </label>
                  <p className="text-[0.68rem] text-[#B6A99F]">{order.provider === "paypal" ? "This will refund the customer via PayPal." : "This will refund the customer's card via Stripe."}</p>
                  <button onClick={doRefund} disabled={busy === "refund"} data-testid="refund-submit"
                    className="flex items-center gap-1.5 rounded-full bg-[#C0392B] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-white disabled:opacity-60">
                    {busy === "refund" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Refund {refund.amount ? fmt(parseFloat(refund.amount) || 0) : "full"}
                  </button>
                </div>
              </Panel>
            )}

            <Panel title={order.payment_status === "refunded" ? "Refunded" : (order.payment_status === "paid" ? "Paid" : "Payment")}>
              <SummaryRow label="Subtotal" sub={`${(order.items || []).reduce((s, i) => s + i.quantity, 0)} items`} value={fmt(order.subtotal || 0)} />
              {order.discount > 0 && <SummaryRow label="Discount" sub={order.discount_code || order.promo_code} value={`- ${fmt(order.discount)}`} />}
              {order.code_discount > 0 && <SummaryRow label="Code discount" sub={order.discount_code} value={`- ${fmt(order.code_discount)}`} />}
              <SummaryRow label="Shipping" sub="Free express" value={fmt(order.shipping || 0)} />
              <div className="my-2 border-t border-[#EDE4D4]" />
              <SummaryRow label="Total" value={fmt(order.total || 0)} strong />
              <SummaryRow label="Paid" value={fmt(order.paid_amount || 0)} />
              {order.refunded_total > 0 && <SummaryRow label="Refunded" value={`- ${fmt(order.refunded_total)}`} />}
              <div className="my-2 border-t border-[#EDE4D4]" />
              <SummaryRow label="Net payment" value={fmt(order.net_payment ?? order.paid_amount ?? 0)} strong />
            </Panel>

            <Panel title="Fulfilment">
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid="ship-tracking" placeholder="Tracking number (optional)"
                className="w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none" />
              <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Carrier"
                className="mt-2 w-full rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none" />
              <div className="mt-3 flex gap-2">
                <button onClick={ship} disabled={busy === "ship"} data-testid="ship-order-btn"
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#232222] py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] disabled:opacity-60">
                  {busy === "ship" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />} {order.status === "shipped" ? "Re-send shipped" : "Mark shipped"}
                </button>
                <button onClick={() => setLabel(true)} data-testid="open-label-btn"
                  className="flex items-center justify-center gap-1.5 rounded-full border border-[#DACDB4] px-4 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#6E6168] hover:border-[#FBC4A8]">
                  <Printer className="h-3.5 w-3.5" /> Label
                </button>
              </div>
            </Panel>

            {/* Timeline */}
            <Panel title="Timeline">
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }} data-testid="timeline-note-input"
                  placeholder="Leave a comment…" className="flex-1 rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2.5 text-sm focus:border-[#FBC4A8] focus:outline-none" />
                <button onClick={addNote} disabled={busy === "note" || !note.trim()} data-testid="timeline-note-post"
                  className="flex items-center gap-1.5 rounded-full bg-[#232222] px-4 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] disabled:opacity-50">
                  {busy === "note" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </button>
              </div>
              <div className="mt-4 space-y-3" data-testid="timeline-events">
                {(order.timeline || []).map((ev, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 flex-none rounded-full bg-[#FBC4A8]" />
                    <div className="flex-1">
                      <p className="text-sm text-[#2C2B2C]">{ev.author ? <span className="font-medium">{ev.author}: </span> : null}{ev.message}</p>
                      <p className="text-[0.68rem] text-[#B6A99F]">{fmtDateTime(ev.at)}</p>
                    </div>
                  </div>
                ))}
                {(order.timeline || []).length === 0 && <p className="text-sm text-[#B6A99F]">No events yet.</p>}
              </div>
            </Panel>
          </div>

          {/* RIGHT */}
          <div className="space-y-5">
            <Panel title="Customer">
              {cust.name ? (
                <>
                  <p className="text-sm font-medium text-[#2C2B2C]">{cust.name}</p>
                  <p className="mt-0.5 text-xs text-[#8A7F86]">{order.conversion?.orders_count || 1} order(s)</p>
                  <div className="mt-3">
                    <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-[#8A7F86]">Contact</p>
                    <p className="mt-1 text-sm text-[#3B6FB0]">{cust.email}</p>
                    <p className="text-sm text-[#6E6168]">{cust.phone || "No phone number"}</p>
                  </div>
                  <div className="mt-3">
                    <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-[#8A7F86]">Shipping address</p>
                    <p className="mt-1 text-sm text-[#6E6168]">{cust.name}</p>
                    <p className="text-sm text-[#6E6168]">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                    <p className="text-sm text-[#6E6168]">{addr.suburb} {addr.state} {addr.postcode}</p>
                    <p className="text-sm text-[#6E6168]">{addr.country === "AU" ? "Australia" : addr.country}</p>
                  </div>
                  <div className="mt-3">
                    <p className="text-[0.62rem] font-semibold uppercase tracking-wider text-[#8A7F86]">Billing address</p>
                    <p className="mt-1 text-sm text-[#6E6168]">Same as shipping address</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#6E6168]">{order.email} <span className="text-[#B6A99F]">(no saved address)</span></p>
              )}
            </Panel>

            {/* Conversion */}
            <Panel title="Conversion summary">
              <div className="space-y-2 text-sm text-[#2C2B2C]" data-testid="conversion-summary">
                <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#FBC4A8]" /> {order.conversion?.is_first_order ? "This is their 1st order" : `Returning customer · ${order.conversion?.orders_count} orders`}</p>
                <p className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#FBC4A8]" /> Lifetime spend {fmt(order.conversion?.lifetime_spend || 0)}</p>
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#FBC4A8]" /> First order {fmtDate(order.conversion?.first_order_at)}</p>
              </div>
            </Panel>

            {/* Tags */}
            <Panel title="Tags">
              <div className="flex flex-wrap gap-2" data-testid="order-tags">
                {(order.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 rounded-full bg-[#F2E8E6] px-3 py-1 text-xs text-[#2C2B2C]">
                    {t}<button onClick={() => removeTag(t)} data-testid={`tag-remove-${t}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} data-testid="tag-input"
                  placeholder="Add tag" className="flex-1 rounded-xl border border-[#E5DBC8] bg-white px-3.5 py-2 text-sm focus:border-[#FBC4A8] focus:outline-none" />
                <button onClick={addTag} data-testid="tag-add" className="rounded-full border border-[#DACDB4] px-4 text-[0.62rem] uppercase tracking-wider text-[#6E6168]">Add</button>
              </div>
            </Panel>
          </div>
        </div>
      </div>
      {label && <ShippingLabel order={order} shipping={shipping} onClose={() => setLabel(false)} />}
      {editing && <OrderFormModal mode="edit" order={order} onClose={() => setEditing(false)} onSaved={() => { load(); onUpdated(); }} />}
    </div>
  );
}

export function OrdersPanel() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/orders/bulk-import", fd);
      if (data.created) toast.success(`Imported ${data.created} order(s)`);
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) skipped — check row ${data.errors[0].row}: ${data.errors[0].error}`);
      if (!data.created && !data.errors?.length) toast.error("No rows found in that file");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Import failed"); }
    finally { setImporting(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await adminApi.get("/admin/orders/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "orders-import-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not download template"); }
  };

  const load = async () => {
    const [o, s] = await Promise.all([adminApi.get("/admin/orders"), adminApi.get("/admin/settings")]);
    setStats(o.data.stats);
    setOrders(o.data.orders);
    setShipping(s.data.shipping);
    setSelected([]);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setSelected((p) => (p.length === orders.length ? [] : orders.map((o) => o.id)));
  const bulkDelete = async () => {
    if (!selected.length || !window.confirm(`Delete ${selected.length} order(s)? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const { data } = await adminApi.post("/admin/orders/bulk-delete", { ids: selected });
      toast.success(`Deleted ${data.deleted} order(s)`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Delete failed"); }
    finally { setDeleting(false); }
  };

  if (loading) return <Spinner />;

  const allChecked = orders.length > 0 && selected.length === orders.length;

  return (
    <div data-testid="orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-light text-[#2C2B2C]">Orders</h1>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImportFile} className="hidden" data-testid="import-csv-input" />
          <button onClick={downloadTemplate} data-testid="csv-template-btn"
            className="text-[0.62rem] uppercase tracking-wider text-[#8A7F86] underline underline-offset-4 hover:text-[#2C2B2C]">CSV template</button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} data-testid="import-orders-btn"
            className="flex items-center gap-1.5 rounded-full border border-[#DACDB4] bg-white px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#2C2B2C] hover:border-[#FBC4A8] disabled:opacity-60">
            {importing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Import CSV
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-order-btn"
            className="flex items-center gap-1.5 rounded-full bg-[#232222] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-[#F5EFE3] hover:opacity-90">
            <Plus className="h-3.5 w-3.5" /> Add order
          </button>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats && STAT_CARDS(stats).map(([Icon, label, val]) => (
          <Card key={label} className="!p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#EFE7D6] text-[#FBC4A8]"><Icon className="h-4 w-4" /></span>
            <p className="mt-3 font-display text-2xl text-[#2C2B2C]">{val}</p>
            <p className="mt-0.5 text-xs uppercase tracking-wider text-[#8A7F86]">{label}</p>
          </Card>
        ))}
      </div>

      {selected.length > 0 && (
        <div className="mt-6 flex items-center justify-between rounded-2xl border border-[#E3B4AC] bg-[#FBEEEC] px-5 py-3" data-testid="orders-bulk-bar">
          <span className="text-sm font-medium text-[#2C2B2C]">{selected.length} selected</span>
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected([])} className="text-[0.62rem] uppercase tracking-wider text-[#6E6168] hover:text-[#2C2B2C]">Clear</button>
            <button onClick={bulkDelete} disabled={deleting} data-testid="orders-bulk-delete"
              className="flex items-center gap-1.5 rounded-full bg-[#C0392B] px-5 py-2.5 text-[0.62rem] uppercase tracking-wider text-white hover:bg-[#A93226] disabled:opacity-60">
              {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-3xl border border-[#E5DBC8] bg-white" data-testid="admin-orders-table">
        {orders.length === 0 ? <p className="p-10 text-center text-[#8A7F86]">No orders yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-[#E5DBC8] text-[0.6rem] uppercase tracking-wider text-[#8A7F86]">
                <tr>
                  <th className="px-5 py-4"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="orders-select-all" className="h-4 w-4 cursor-pointer accent-[#C0392B]" /></th>
                  <th className="px-5 py-4">Date</th><th className="px-5 py-4">Customer</th><th className="px-5 py-4">Items</th><th className="px-5 py-4">Total</th><th className="px-5 py-4">Payment</th><th className="px-5 py-4">Fulfilment</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} onClick={() => setActiveId(o.id)} data-testid={`order-row-${o.id}`}
                    className={`cursor-pointer border-b border-[#EDE4D4] transition-colors last:border-0 hover:bg-[#FBF7F3] ${selected.includes(o.id) ? "bg-[#FBEEEC]" : ""}`}>
                    <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} data-testid={`order-select-${o.id}`} className="h-4 w-4 cursor-pointer accent-[#C0392B]" />
                    </td>
                    <td className="px-5 py-4 text-[#8A7F86]">{fmtDate(o.created_at)}</td>
                    <td className="px-5 py-4 text-[#2C2B2C]">{o.customer?.name || o.email}</td>
                    <td className="px-5 py-4 text-[#8A7F86]">{(o.items || []).reduce((s, i) => s + i.quantity, 0)} item(s)</td>
                    <td className="px-5 py-4 font-medium text-[#2C2B2C]">{fmt(o.total)}</td>
                    <td className="px-5 py-4"><StatusBadge status={o.payment_status} /></td>
                    <td className="px-5 py-4"><StatusBadge status={o.status === "shipped" ? "shipped" : (o.fulfillment_status || "unfulfilled")} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {activeId && <OrderDetail orderId={activeId} shipping={shipping} onClose={() => setActiveId(null)} onUpdated={load} />}
      {showAdd && <OrderFormModal mode="create" onClose={() => setShowAdd(false)} onSaved={load} />}
    </div>
  );
}
